/**
 * Animated stage light effects layered over the hero section.
 * Renders a film-grain texture plus 8 independently animated light leak /
 * spotlight / lens flare elements. All animations are disabled when the user
 * prefers reduced motion or the hero is scrolled out of view.
 */
import { motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";

const HeroEffects = () => {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { margin: "100px" });
  const prefersReducedMotion = useReducedMotion();

  // Skip all blur animations on mobile — biggest GPU drain
  const isMobile = typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches;
  if (isMobile) return null;

  return (
    <>

      {/* Animated light leaks - z-index 20 to appear above images */}
      <div ref={ref} className="absolute inset-0 overflow-hidden pointer-events-none z-20" aria-hidden="true">
        {isInView && !prefersReducedMotion && (
          <>
            {/* Top-left warm light leak */}
            <motion.div
              className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 rounded-full"
              style={{
                background: "radial-gradient(ellipse at center, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 40%, transparent 70%)",
                filter: "blur(60px)",
              }}
              animate={{
                opacity: [0.4, 0.8, 0.5, 0.9, 0.4],
                scale: [1, 1.2, 1.1, 1.3, 1],
                x: ["0%", "10%", "5%", "15%", "0%"],
                y: ["0%", "5%", "-5%", "10%", "0%"],
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Top-right light flare */}
            <motion.div
              className="absolute -top-1/4 -right-1/4 w-2/3 h-2/3"
              style={{
                background: "radial-gradient(ellipse at center, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.08) 50%, transparent 70%)",
                filter: "blur(80px)",
              }}
              animate={{
                opacity: [0.3, 0.7, 0.4, 0.8, 0.3],
                scale: [1, 1.15, 1.05, 1.2, 1],
                rotate: [0, 5, -3, 8, 0],
              }}
              transition={{
                duration: 15,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2,
              }}
            />

            {/* Stage spotlight - left */}
            <motion.div
              className="absolute top-0 left-1/4 w-40 h-full"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 30%, transparent 60%)",
                filter: "blur(25px)",
                transformOrigin: "top center",
              }}
              animate={{
                opacity: [0, 0.6, 0.3, 0.7, 0],
                scaleX: [0.5, 1.5, 0.8, 1.2, 0.5],
                skewX: ["-5deg", "5deg", "-3deg", "8deg", "-5deg"],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 1,
              }}
            />

            {/* Stage spotlight - center (main) */}
            <motion.div
              className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-full"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.08) 30%, rgba(255,255,255,0.02) 50%, transparent 70%)",
                filter: "blur(30px)",
                transformOrigin: "top center",
              }}
              animate={{
                opacity: [0.4, 0.8, 0.3, 0.9, 0.4],
                scaleX: [1, 1.3, 0.9, 1.4, 1],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Stage spotlight - right */}
            <motion.div
              className="absolute top-0 right-1/4 w-40 h-full"
              style={{
                background: "linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 30%, transparent 60%)",
                filter: "blur(25px)",
                transformOrigin: "top center",
              }}
              animate={{
                opacity: [0.1, 0.7, 0.4, 0.6, 0.1],
                scaleX: [0.8, 1.2, 1, 1.5, 0.8],
                skewX: ["5deg", "-5deg", "3deg", "-8deg", "5deg"],
              }}
              transition={{
                duration: 9,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 3,
              }}
            />

            {/* Bright flare burst */}
            <motion.div
              className="absolute top-1/3 left-1/3 w-80 h-80"
              style={{
                background: "radial-gradient(circle at center, rgba(255,255,255,0.35) 0%, rgba(255,255,255,0.1) 30%, transparent 50%)",
                filter: "blur(15px)",
              }}
              animate={{
                opacity: [0, 0.5, 0, 0.7, 0, 0.4, 0],
                scale: [0.5, 1.5, 0.5, 2, 0.5, 1.2, 0.5],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />

            {/* Horizontal lens flare */}
            <motion.div
              className="absolute top-1/2 left-0 w-full h-2"
              style={{
                background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 20%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0.15) 80%, transparent 100%)",
                filter: "blur(6px)",
              }}
              animate={{
                opacity: [0, 0.6, 0, 0.8, 0],
                scaleY: [1, 4, 1, 5, 1],
                y: ["-50%", "-30%", "-50%", "-70%", "-50%"],
              }}
              transition={{
                duration: 18,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 5,
              }}
            />

            {/* Moving side light leak - simulates stage light sweep */}
            <motion.div
              className="absolute top-0 w-96 h-full"
              style={{
                background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 30%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.1) 70%, transparent 100%)",
                filter: "blur(40px)",
              }}
              animate={{
                left: ["-20%", "100%"],
                opacity: [0, 0.5, 0.7, 0.5, 0],
              }}
              transition={{
                duration: 25,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 8,
              }}
            />
          </>
        )}
      </div>
    </>
  );
};

export default HeroEffects;
